import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { agentApi } from '../../services/api';

type SessionLike = {
  id?: string | null;
  workspace?: string | null;
  workspace_task?: string | null;
};

interface AgentWorkspaceInspectorProps {
  open: boolean;
  sessionId?: string | null;
  sessionData?: SessionLike | null;
}

type ArtifactContentState = {
  artifactId: string;
  content: string;
  loadedThrough: number;
  nextOffset: number | null;
};

const ARTIFACT_PAGE_CHARS = 40000;
const MANIFEST_PAGE_SIZE = 50;

const number = (value: unknown): number => {
  const result = Number(value);
  return Number.isFinite(result) ? result : 0;
};

const shortId = (value?: string | null): string => {
  if (!value) return 'None';
  return value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-5)}` : value;
};

const label = (value: string): string => (
  value.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())
);

const JsonBlock = ({ value }: { value: unknown }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.25,
      borderRadius: 1,
      bgcolor: 'action.hover',
      color: 'text.primary',
      fontSize: 12,
      lineHeight: 1.45,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      maxHeight: 320,
      overflow: 'auto',
    }}
  >
    {JSON.stringify(value, null, 2)}
  </Box>
);

const TextBlock = ({ value }: { value: string }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.25,
      borderRadius: 1,
      bgcolor: 'action.hover',
      color: 'text.primary',
      fontSize: 12,
      lineHeight: 1.45,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      maxHeight: 320,
      overflow: 'auto',
    }}
  >
    {value}
  </Box>
);

const Metric = ({ title, value }: { title: string; value: React.ReactNode }) => (
  <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">{title}</Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{value}</Typography>
  </Box>
);

export default function AgentWorkspaceInspector({
  open,
  sessionId,
  sessionData,
}: AgentWorkspaceInspectorProps) {
  const [tab, setTab] = useState(0);
  const [workspaceId, setWorkspaceId] = useState<string | null>(
    sessionData?.workspace || null
  );
  const [workspaceTaskId, setWorkspaceTaskId] = useState<string | null>(
    sessionData?.workspace_task || null
  );
  const [workspace, setWorkspace] = useState<Record<string, any> | null>(null);
  const [contextPacket, setContextPacket] = useState<Record<string, any> | null>(null);
  const [artifacts, setArtifacts] = useState<Record<string, any>[]>([]);
  const [delegation, setDelegation] = useState<Record<string, any>>({
    tasks: [],
    executions: [],
  });
  const [usage, setUsage] = useState<Record<string, any>>({
    totals: {},
    component_totals: {},
    manifests: [],
    manifest_pagination: {
      count: 0,
      offset: 0,
      limit: MANIFEST_PAGE_SIZE,
      next_offset: null,
    },
  });
  const [artifactQuery, setArtifactQuery] = useState('');
  const [selectedArtifact, setSelectedArtifact] = useState<Record<string, any> | null>(null);
  const [artifactContent, setArtifactContent] = useState<ArtifactContentState | null>(null);
  const [loading, setLoading] = useState(false);
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setWorkspaceId(sessionData?.workspace || null);
    setWorkspaceTaskId(sessionData?.workspace_task || null);
  }, [sessionData?.workspace, sessionData?.workspace_task, sessionId]);

  const load = useCallback(async () => {
    if (!open || !sessionId) return;
    setLoading(true);
    setError(null);
    try {
      let resolvedWorkspaceId = sessionData?.workspace || workspaceId;
      let resolvedTaskId = sessionData?.workspace_task || workspaceTaskId;
      if (!resolvedWorkspaceId) {
        const session = await agentApi.getSession(sessionId, {
          includeMessages: false,
          includeExecutions: false,
          includeSystemSnapshot: false,
        });
        resolvedWorkspaceId = session.workspace || null;
        resolvedTaskId = session.workspace_task || null;
      }
      if (!resolvedWorkspaceId) {
        setWorkspace(null);
        setError('This conversation does not have a workspace yet.');
        return;
      }
      setWorkspaceId(resolvedWorkspaceId);
      setWorkspaceTaskId(resolvedTaskId);
      const [workspaceResult, contextResult, artifactResult, delegationResult, usageResult] =
        await Promise.all([
          agentApi.getWorkspace(resolvedWorkspaceId),
          agentApi.getWorkspaceContext(resolvedWorkspaceId, resolvedTaskId),
          agentApi.getWorkspaceArtifacts(resolvedWorkspaceId, { limit: 50 }),
          agentApi.getWorkspaceDelegation(resolvedWorkspaceId),
          agentApi.getWorkspaceUsage(resolvedWorkspaceId, {
            manifest_limit: MANIFEST_PAGE_SIZE,
          }),
        ]);
      setWorkspace(workspaceResult);
      setContextPacket(contextResult);
      setArtifacts(artifactResult.results || []);
      setDelegation(delegationResult || { tasks: [], executions: [] });
      setUsage(usageResult || {
        totals: {},
        component_totals: {},
        manifests: [],
        manifest_pagination: {
          count: 0,
          offset: 0,
          limit: MANIFEST_PAGE_SIZE,
          next_offset: null,
        },
      });
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.detail
        || requestError?.response?.data?.error
        || requestError?.message
        || 'Failed to load workspace inspector.'
      );
    } finally {
      setLoading(false);
    }
  }, [
    open,
    sessionData?.workspace,
    sessionData?.workspace_task,
    sessionId,
    workspaceId,
    workspaceTaskId,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const searchArtifacts = useCallback(async () => {
    if (!workspaceId) return;
    setArtifactLoading(true);
    setError(null);
    try {
      const response = await agentApi.getWorkspaceArtifacts(workspaceId, {
        query: artifactQuery.trim() || undefined,
        limit: 100,
      });
      setArtifacts(response.results || []);
    } catch (requestError: any) {
      setError(requestError?.message || 'Artifact search failed.');
    } finally {
      setArtifactLoading(false);
    }
  }, [artifactQuery, workspaceId]);

  const readArtifact = useCallback(async (artifact: Record<string, any>) => {
    if (!workspaceId || !artifact?.id) return;
    setSelectedArtifact(artifact);
    setArtifactContent(null);
    setArtifactLoading(true);
    try {
      const response = await agentApi.getWorkspaceArtifactContent(
        workspaceId,
        artifact.id,
        { max_chars: ARTIFACT_PAGE_CHARS, offset: 0 }
      );
      const content = String(response.content || '');
      setArtifactContent({
        artifactId: String(artifact.id),
        content,
        loadedThrough: number(
          response.next_offset ?? (number(response.offset) + content.length)
        ),
        nextOffset: response.next_offset ?? null,
      });
    } catch (requestError: any) {
      setError(requestError?.message || 'Artifact read failed.');
    } finally {
      setArtifactLoading(false);
    }
  }, [workspaceId]);

  const loadMoreArtifact = useCallback(async () => {
    if (
      !workspaceId
      || !selectedArtifact?.id
      || artifactContent?.nextOffset == null
    ) {
      return;
    }
    const artifactId = String(selectedArtifact.id);
    const offset = artifactContent.nextOffset;
    setArtifactLoading(true);
    setError(null);
    try {
      const response = await agentApi.getWorkspaceArtifactContent(
        workspaceId,
        artifactId,
        { max_chars: ARTIFACT_PAGE_CHARS, offset }
      );
      const nextContent = String(response.content || '');
      setArtifactContent((current) => {
        if (!current || current.artifactId !== artifactId) return current;
        return {
          artifactId,
          content: `${current.content}${nextContent}`,
          loadedThrough: number(
            response.next_offset
            ?? (number(response.offset) + nextContent.length)
          ),
          nextOffset: response.next_offset ?? null,
        };
      });
    } catch (requestError: any) {
      setError(requestError?.message || 'Loading the next artifact page failed.');
    } finally {
      setArtifactLoading(false);
    }
  }, [artifactContent?.nextOffset, selectedArtifact?.id, workspaceId]);

  const loadMoreManifests = useCallback(async () => {
    const nextOffset = usage.manifest_pagination?.next_offset;
    if (!workspaceId || nextOffset == null) return;
    setManifestLoading(true);
    setError(null);
    try {
      const response = await agentApi.getWorkspaceUsage(workspaceId, {
        manifest_offset: nextOffset,
        manifest_limit: MANIFEST_PAGE_SIZE,
      });
      setUsage((current) => {
        const rows = [...(current.manifests || []), ...(response.manifests || [])];
        const seen = new Set<string>();
        return {
          ...response,
          manifests: rows.filter((manifest: any) => {
            const id = String(manifest.id);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          }),
        };
      });
    } catch (requestError: any) {
      setError(requestError?.message || 'Loading more model-call manifests failed.');
    } finally {
      setManifestLoading(false);
    }
  }, [usage.manifest_pagination?.next_offset, workspaceId]);

  const context = contextPacket?.packet || {};
  const componentRows = useMemo(() => {
    const components = usage.component_totals || {};
    const normalized: Record<string, number> = {
      system_prompt: number(components.system_prompt),
      tool_schemas: number(components.tool_schemas),
      history: (
        number(components.user_and_history)
        + number(components.conversation_history)
        + number(components.session_summary)
      ),
      memory: number(components.memory) + number(components.tool_memory),
      tool_output: number(components.tool_output),
      context_packet: number(components.context_packet),
    };
    const accounted = new Set([
      'system_prompt',
      'tool_schemas',
      'user_and_history',
      'conversation_history',
      'session_summary',
      'memory',
      'tool_memory',
      'tool_output',
      'context_packet',
    ]);
    Object.entries(components).forEach(([name, value]) => {
      if (!accounted.has(name)) normalized[name] = number(value);
    });
    return Object.entries(normalized)
      .filter(([, value]) => value > 0)
      .sort((left, right) => right[1] - left[1]);
  }, [usage.component_totals]);
  const maxComponentTokens = Math.max(...componentRows.map(([, tokens]) => tokens), 1);

  const taskLevels = useMemo(() => {
    const tasks = delegation.tasks || [];
    const taskMap = new Map(tasks.map((task: any) => [String(task.id), task]));
    const depth = (task: any): number => {
      let level = 0;
      let current = task;
      const visited = new Set<string>();
      while (current?.parent_task && level < 20) {
        const parentId = String(current.parent_task);
        if (visited.has(parentId)) break;
        visited.add(parentId);
        current = taskMap.get(parentId);
        level += 1;
      }
      return level;
    };
    return tasks.map((task: any) => ({ ...task, depth: depth(task) }));
  }, [delegation.tasks]);

  const taskCosts = useMemo(() => {
    const totals = new Map<string, {
      directTokens: number;
      directCost: number;
      inclusiveTokens: number;
      inclusiveCost: number;
      executions: number;
    }>();
    const parentByTask = new Map<string, string | null>(
      (delegation.tasks || []).map((task: any) => [
        String(task.id),
        task.parent_task ? String(task.parent_task) : null,
      ])
    );
    const row = (taskId: string) => {
      const current = totals.get(taskId) || {
        directTokens: 0,
        directCost: 0,
        inclusiveTokens: 0,
        inclusiveCost: 0,
        executions: 0,
      };
      totals.set(taskId, current);
      return current;
    };
    (delegation.executions || []).forEach((execution: any) => {
      const taskId = String(execution.task_id || '');
      const tokens = number(execution.total_tokens);
      const cost = number(execution.total_cost);
      const current = row(taskId);
      current.directTokens += tokens;
      current.directCost += cost;
      current.executions += 1;
      let ancestorId: string | null = taskId;
      const visited = new Set<string>();
      while (ancestorId && !visited.has(ancestorId)) {
        visited.add(ancestorId);
        const ancestor = row(ancestorId);
        ancestor.inclusiveTokens += tokens;
        ancestor.inclusiveCost += cost;
        ancestorId = parentByTask.get(ancestorId) || null;
      }
    });
    return totals;
  }, [delegation.executions, delegation.tasks]);

  if (!sessionId) {
    return (
      <Alert severity="info">Start or load a conversation to create its agent workspace.</Alert>
    );
  }

  return (
    <Box sx={{ minHeight: 260 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {workspaceId ? `Workspace ${shortId(workspaceId)}` : 'Resolving workspace…'}
        </Typography>
        <Button size="small" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 40, mb: 1.5 }}
      >
        <Tab label="Overview" sx={{ minHeight: 40 }} />
        <Tab label="Context" sx={{ minHeight: 40 }} />
        <Tab label={`Artifacts (${artifacts.length})`} sx={{ minHeight: 40 }} />
        <Tab label="Delegation" sx={{ minHeight: 40 }} />
        <Tab label="Context & cost" sx={{ minHeight: 40 }} />
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {tab === 0 && workspace && (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1 }}>
            <Metric title="Tasks" value={number(workspace.task_count).toLocaleString()} />
            <Metric title="Executions" value={number(workspace.execution_count).toLocaleString()} />
            <Metric title="Artifacts" value={number(workspace.artifact_count).toLocaleString()} />
            <Metric title="Cost" value={`$${number(workspace.total_cost).toFixed(4)}`} />
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">Objective</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>
            {workspace.objective || context.objective || 'No objective recorded yet.'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary">Workspace</Typography>
            <Typography variant="caption">{workspace.id}</Typography>
            <Typography variant="caption" color="text.secondary">Current task</Typography>
            <Typography variant="caption">{workspaceTaskId || 'None'}</Typography>
            <Typography variant="caption" color="text.secondary">Context version</Typography>
            <Typography variant="caption">{contextPacket?.version || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="caption">{workspace.status}</Typography>
          </Box>
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {[
            'objective',
            'constraints',
            'authority',
            'resolved_resources',
            'evidence',
            'decisions',
            'completed_actions',
            'open_questions',
            'plan',
          ].map((field) => (
            <Box key={field}>
              <Typography variant="caption" color="text.secondary">
                {label(field)}
              </Typography>
              <JsonBlock value={context[field] ?? (field === 'objective' ? '' : [])} />
            </Box>
          ))}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Box
            component="form"
            onSubmit={(event) => {
              event.preventDefault();
              void searchArtifacts();
            }}
            sx={{ display: 'flex', gap: 1, mb: 1.25 }}
          >
            <TextField
              value={artifactQuery}
              onChange={(event) => setArtifactQuery(event.target.value)}
              size="small"
              fullWidth
              placeholder="Search tool inputs, outputs, summaries, paths…"
            />
            <Button type="submit" variant="outlined" disabled={artifactLoading}>
              Search
            </Button>
          </Box>
          {artifactLoading && <LinearProgress sx={{ mb: 1 }} />}
          <Box sx={{ display: 'grid', gridTemplateColumns: selectedArtifact ? 'minmax(220px, .8fr) minmax(0, 1.2fr)' : '1fr', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 390, overflow: 'auto' }}>
              {artifacts.map((artifact) => (
                <Button
                  key={artifact.id}
                  onClick={() => void readArtifact(artifact)}
                  variant={selectedArtifact?.id === artifact.id ? 'contained' : 'outlined'}
                  color="inherit"
                  sx={{ display: 'block', textAlign: 'left', textTransform: 'none', p: 1 }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                    {artifact.tool_name || label(artifact.artifact_type || 'artifact')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                    {artifact.virtual_path}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {artifact.summary || `${number(artifact.serialized_chars).toLocaleString()} characters`}
                  </Typography>
                </Button>
              ))}
              {!artifacts.length && (
                <Typography variant="body2" color="text.secondary">
                  No matching workspace artifacts.
                </Typography>
              )}
            </Box>
            {selectedArtifact && (
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
                  <Chip size="small" label={selectedArtifact.freshness_class || 'UNKNOWN'} />
                  <Chip size="small" label={shortId(selectedArtifact.id)} />
                  <Chip size="small" label={`${number(selectedArtifact.serialized_chars).toLocaleString()} chars`} />
                </Box>
                {artifactContent ? (
                  <>
                    <TextBlock value={artifactContent.content} />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mt: 0.75,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Loaded through character {artifactContent.loadedThrough.toLocaleString()}
                      </Typography>
                      {artifactContent.nextOffset != null && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => void loadMoreArtifact()}
                          disabled={artifactLoading}
                        >
                          Load next {ARTIFACT_PAGE_CHARS.toLocaleString()}
                        </Button>
                      )}
                    </Box>
                  </>
                ) : (
                  <JsonBlock value={selectedArtifact.preview} />
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {tab === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {taskLevels.map((task: any) => {
            const totals = taskCosts.get(String(task.id)) || {
              directTokens: 0,
              directCost: 0,
              inclusiveTokens: 0,
              inclusiveCost: 0,
              executions: 0,
            };
            return (
              <Box
                key={task.id}
                sx={{
                  ml: Math.min(task.depth, 6) * 2,
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderLeftWidth: task.depth ? 3 : 1,
                  borderLeftColor: task.depth ? 'primary.main' : 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                      {task.assigned_agent_name || 'Unassigned task'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.objective || shortId(task.id)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      ${totals.inclusiveCost.toFixed(4)} · {totals.inclusiveTokens.toLocaleString()} tokens
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totals.executions} direct execution{totals.executions === 1 ? '' : 's'} · {task.status}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
          {!taskLevels.length && (
            <Typography variant="body2" color="text.secondary">
              No workspace tasks have been recorded.
            </Typography>
          )}
        </Box>
      )}

      {tab === 4 && (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1, mb: 1.5 }}>
            <Metric title="Execution tokens" value={number(usage.totals?.total_tokens).toLocaleString()} />
            <Metric title="Estimated context" value={number(usage.totals?.estimated_context_tokens).toLocaleString()} />
            <Metric title="Cached input" value={number(usage.totals?.cached_input_tokens).toLocaleString()} />
            <Metric title="Workspace cost" value={`$${number(usage.totals?.total_cost).toFixed(4)}`} />
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Prompt composition across model calls</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {componentRows.map(([name, tokens]) => (
              <Box key={name}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="caption">{label(name)}</Typography>
                  <Typography variant="caption">{tokens.toLocaleString()} tokens</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(tokens / maxComponentTokens) * 100}
                  sx={{ height: 7, borderRadius: 1 }}
                />
              </Box>
            ))}
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            Model-call manifests ({(usage.manifests || []).length}
            {number(usage.manifest_pagination?.count) > (usage.manifests || []).length
              ? ` of ${number(usage.manifest_pagination?.count).toLocaleString()}`
              : ''})
          </Typography>
          {manifestLoading && <LinearProgress sx={{ mb: 0.75 }} />}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 240, overflow: 'auto' }}>
            {(usage.manifests || []).map((manifest: any) => (
              <Box
                key={manifest.id}
                sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 1, p: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}
              >
                <Typography variant="caption" noWrap>
                  {manifest.agent_name || 'Agent'} · iteration {manifest.iteration}
                </Typography>
                <Typography variant="caption">{number(manifest.estimated_prompt_tokens).toLocaleString()} est.</Typography>
                <Typography variant="caption">{number(manifest.cached_input_tokens).toLocaleString()} cached</Typography>
                <Typography variant="caption">${number(manifest.cost).toFixed(4)}</Typography>
              </Box>
            ))}
          </Box>
          {usage.manifest_pagination?.next_offset != null && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => void loadMoreManifests()}
              disabled={manifestLoading}
              sx={{ mt: 1 }}
            >
              Load more model calls
            </Button>
          )}
        </Box>
      )}

      {!workspace && !loading && !error && (
        <Alert severity="info">No workspace data is available for this conversation.</Alert>
      )}
      {loading && !workspace && (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 180 }}>
          <CircularProgress size={28} />
        </Box>
      )}
    </Box>
  );
}
